/*
 * The matching rule behind every CMMS search box.
 *
 * Lifted out of cmmsSpareParts/sparePartSearch.js when the work order list became the second
 * caller. It lives here rather than in either feature because two search boxes that disagreed
 * about what "bearing a-3" means would be worse than either rule on its own: someone who learns
 * the register's box works one way will type the same thing into the work order box.
 *
 * What is *searched* stays with each feature — the row is theirs to describe, and the register's
 * four text fields have nothing in common with a work order's resolved names and enum labels.
 * Only how a query is matched against that text is shared.
 */

/*
 * Every word must appear somewhere in the row; where is not important.
 *
 * features/activities/components/RecurringActivitiesGrid.jsx — the pattern both boxes are copied
 * from — matches the whole typed string as one substring, which quietly fails the way people
 * actually search: "bearing a-3" finds nothing unless the name happens to sit next to the shelf
 * in the joined text. Requiring each word separately lets someone combine what they remember,
 * from whichever fields they remember it, and still land on the row.
 *
 * `getText` must return the row's searchable text already lower-cased. Doing it per row here
 * instead would lower-case the same strings on every keystroke.
 *
 * An empty or blank query returns the same array, not a copy: no filter is not a filter, and
 * handing back a new array every keystroke would churn the grid for nothing.
 */
export const filterBySearchText = (rows, query, getText) => {
  const tokens = (query ?? "").toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return rows ?? [];

  return (rows ?? []).filter((row) => {
    const text = getText(row);
    return tokens.every((token) => text.includes(token));
  });
};
